import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { setPersistence, browserLocalPersistence, browserSessionPersistence } from "firebase/auth";
import { X, Building2, User, MapPin, ArrowRight, AlertTriangle, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { auth, FIREBASE_READY } from "../lib/firebase";
import { SOCIALPULSE_SSO_READY, startSocialPulseLogin } from "../lib/socialpulse";
import api from "../lib/api";

/* SocialPulse-only auth popup — SocialPulse is the common sign-up/sign-in system
   for the whole network (SocialPulse, IndiaAlumni, TravelerPulse). Old methods
   (Google popup / Phone / Email link) were removed by owner request; existing
   accounts are auto-linked by verified email during the SSO bridge.
   Registration details are stored in localStorage (tp_pending_reg) and applied
   right after the first successful sign-in (by the SSO callback page). */

const GENDERS = ["Female", "Male", "Non-binary", "Prefer not to say"];
const YEARS = (() => { const y = new Date().getFullYear() - 13; return Array.from({ length: 95 }, (_, i) => y - i); })();
const POLICIES = [
  ["terms", "Terms of Service", "/legal/terms"],
  ["privacy", "Privacy Policy", "/legal/privacy"],
];

export async function applyPendingRegistration() {
  const raw = localStorage.getItem("tp_pending_reg");
  if (!raw) return;
  try {
    const reg = JSON.parse(raw);
    if (auth?.currentUser) localStorage.setItem("token", await auth.currentUser.getIdToken());
    if (reg.profile) await api.post("/auth/profile", reg.profile).catch(() => toast.warning("We couldn't save your profile details — you can edit them in your profile"));
    if (reg.business?.name) {
      const { data } = await api.post("/marketplace/businesses/register", reg.business);
      toast.success(`Business profile created for ${data.name} — manage it from your profile`);
    }
  } catch { /* profile application is best-effort; user can edit later */ }
  localStorage.removeItem("tp_pending_reg");
}

const Brand = () => <>Traveler<span className="text-primary">Pulse</span><sup className="text-[10px] font-400 ml-0.5">TM</sup></>;

export default function AuthModal({ view }) {
  const nav = useNavigate();
  const isRegister = view === "register";
  const [keep, setKeep] = useState(() => localStorage.getItem("tp_keep") !== "0");
  const [busy, setBusy] = useState(false);

  // register state
  const [regTab, setRegTab] = useState("individual");
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [gender, setGender] = useState("");
  const [birthYear, setBirthYear] = useState("");
  const [loc, setLoc] = useState({ country: "", region: "", city: "" });
  const [locSet, setLocSet] = useState(false);
  const [biz, setBiz] = useState({ name: "", address: "", contact_person: "", business_email: "", business_phone: "" });
  const [accepted, setAccepted] = useState({});

  const allAccepted = POLICIES.every(([k]) => accepted[k]);
  const regReady = !isRegister || (allAccepted && (regTab !== "business" || biz.name.trim().length >= 2));
  const blockReason = !allAccepted ? "Tick every policy box to enable the sign-up buttons"
    : "Enter your business name to continue";

  const setKeepChoice = (v) => { setKeep(v); localStorage.setItem("tp_keep", v ? "1" : "0"); };

  const preparePending = () => {
    if (!isRegister) { localStorage.removeItem("tp_pending_reg"); return; }
    const profile = {
      first_name: first.trim() || undefined, last_name: last.trim() || undefined,
      gender: gender || undefined, birth_year: birthYear || undefined,
      location: locSet || loc.country || loc.city ? loc : undefined,
      account_type: regTab,
    };
    const pending = { profile };
    if (regTab === "business" && biz.name.trim()) {
      pending.business = { ...biz, contact_person: biz.contact_person.trim() || `${first} ${last}`.trim() };
    }
    localStorage.setItem("tp_pending_reg", JSON.stringify(pending));
  };

  const beforeAuth = async () => {
    if (isRegister && !regReady) { toast.error(blockReason); return false; }
    preparePending();
    localStorage.setItem("tp_last_activity", String(Date.now()));
    try { await setPersistence(auth, keep ? browserLocalPersistence : browserSessionPersistence); } catch {}
    return true;
  };

  const fail = (e) => toast.error(e?.code?.replace("auth/", "").replace(/-/g, " ") || e?.message || "Failed");

  const socialpulse = () => {
    // Backend-driven OIDC flow (2026-02 hub-operator contract): full-page redirect
    // to /api/auth/socialpulse/init which stashes PKCE verifier and 302s the user
    // to the hub. No client-side state to manage. Handles return_to correctly.
    const next = new URLSearchParams(window.location.search).get("next");
    const here = window.location.pathname + window.location.search;
    const returnTo = next && next.startsWith("/") ? next : (here && here !== "/login" && here !== "/register" ? here : "/");
    // Stash a short marker so SsoRecoveryBanner can detect a stalled flow if the
    // SocialPulse hub forgets to send the user back after Google OAuth.
    try {
      sessionStorage.setItem("sp_sso_pending", JSON.stringify({
        started_at: Date.now(), return_to: returnTo,
      }));
    } catch { /* storage full */ }
    window.location.assign(`/api/auth/socialpulse/init?return_to=${encodeURIComponent(returnTo)}`);
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-[2px] flex items-start sm:items-center justify-center overflow-y-auto p-3 sm:p-6"
      data-testid="auth-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) nav("/"); }}>
      <div className="bg-white w-full max-w-lg my-4 sm:my-0 max-h-[92vh] overflow-y-auto border border-zinc-300 shadow-2xl relative">
        <button onClick={() => nav("/")} data-testid="auth-modal-close" aria-label="Close"
          className="absolute top-3 right-3 p-1.5 text-zinc-400 hover:text-zinc-800 hover:bg-zinc-100 z-10"><X className="w-5 h-5" /></button>

        <div className="px-6 sm:px-8 pt-8 pb-7">
          {/* Header */}
          <h1 className="font-heading font-900 text-2xl sm:text-3xl tracking-tight" data-testid="auth-title">
            {isRegister ? <>Register &amp; Join <Brand /></> : <>Sign in to <Brand /></>}
          </h1>
          <p className="text-xs font-700 text-rose-700 mt-1">Powered by SocialPulse<sup className="font-400">&reg;</sup></p>
          <p className="text-sm text-muted-foreground mt-2">
            {isRegister ? "Tell us who you are, then continue with SocialPulse." : "One secure account for SocialPulse, IndiaAlumni and TravelerPulse."}
          </p>

          {!FIREBASE_READY && (
            <div className="mt-4 flex gap-2 items-start text-sm text-amber-800 bg-amber-50 border border-amber-200 p-3" data-testid="firebase-not-ready">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> Sign-in isn't fully configured yet.
            </div>
          )}

          {/* Cross link */}
          <button onClick={() => nav(isRegister ? "/login" : "/register")} data-testid="auth-cross-link"
            className="mt-4 w-full flex items-center justify-between gap-2 border border-zinc-300 bg-zinc-50 hover:border-primary px-4 py-2.5 text-sm transition-colors">
            <span className="font-600 text-left">{isRegister ? "Already have an account with SocialPulse?" : <>New to SocialPulse <span className="text-muted-foreground font-400">or TravelerPulse?</span></>}</span>
            <span className="shrink-0 inline-flex items-center gap-1 text-xs font-900 tracking-widest text-primary">{isRegister ? "SIGN / LOG IN" : "REGISTER"} <ArrowRight className="w-3.5 h-3.5" /></span>
          </button>

          {isRegister && (
            <RegisterFields regTab={regTab} setRegTab={setRegTab} first={first} setFirst={setFirst} last={last} setLast={setLast}
              gender={gender} setGender={setGender} birthYear={birthYear} setBirthYear={setBirthYear}
              loc={loc} setLoc={setLoc} locSet={locSet} setLocSet={setLocSet} biz={biz} setBiz={setBiz} />
          )}

          {isRegister && (
            <div className="mt-5 border border-zinc-300 bg-zinc-50/60 p-4" data-testid="auth-policies">
              <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Please read &amp; accept <span className="normal-case tracking-normal">— every item is required to create your account</span></div>
              {POLICIES.map(([k, label, href]) => (
                <label key={k} className="flex items-start gap-2 py-1 text-sm cursor-pointer">
                  <input type="checkbox" checked={!!accepted[k]} onChange={() => setAccepted((a) => ({ ...a, [k]: !a[k] }))}
                    data-testid={`policy-${k}`} className="w-4 h-4 mt-0.5 accent-primary shrink-0" />
                  <span>I have read and accept the <Link to={href} target="_blank" className="text-primary font-600 underline">{label}</Link>.</span>
                </label>
              ))}
              <p className="text-[11px] text-muted-foreground mt-1.5" data-testid="auth-policies-hint">
                {allAccepted ? <span className="inline-flex items-center gap-1 text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> Thanks — sign-up buttons are enabled.</span> : "Tick every box to enable the sign-up buttons."}
              </p>
            </div>
          )}

          {/* SocialPulse SSO — the only sign-in method */}
          <div className={`mt-5 ${isRegister && !regReady ? "opacity-50 pointer-events-none select-none" : ""}`} data-testid="auth-methods">
            <button onClick={socialpulse} disabled={busy || !SOCIALPULSE_SSO_READY || !FIREBASE_READY} data-testid="socialpulse-login-btn"
              className="w-full flex items-center justify-center gap-2 border-2 border-violet-700 bg-violet-600 text-white py-3.5 text-sm font-700 hover:bg-violet-700 transition-colors disabled:opacity-50 rounded-full">
              <Sparkles className="w-4 h-4" /> {busy ? "Redirecting…" : "Continue with SocialPulse"}
            </button>
            <p className="text-xs text-muted-foreground text-center mt-2">You'll be redirected to SocialPulse to {isRegister ? "create your account" : "sign in"}, then brought straight back.</p>
            {!SOCIALPULSE_SSO_READY && (
              <p className="mt-2 text-xs text-amber-700 text-center" data-testid="sso-not-configured">SocialPulse sign-in isn't configured on this deployment.</p>
            )}
          </div>

          {/* Keep signed in */}
          <label className="mt-5 flex items-center gap-2 text-sm font-600 cursor-pointer" data-testid="auth-keep-signed-in">
            <input type="checkbox" checked={keep} onChange={(e) => setKeepChoice(e.target.checked)} className="w-4 h-4 accent-primary" />
            Keep me signed in on this device <span className="text-muted-foreground font-400">(12h idle limit)</span>
          </label>

          <p className="mt-4 text-xs text-muted-foreground text-center">New here? Signing in with SocialPulse also creates your account.</p>
        </div>
      </div>
    </div>
  );
}

function RegisterFields({ regTab, setRegTab, first, setFirst, last, setLast, gender, setGender,
  birthYear, setBirthYear, loc, setLoc, locSet, setLocSet, biz, setBiz }) {
  const inp = "w-full border border-zinc-300 bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary";
  const lbl = "block text-xs font-600 text-zinc-600 mb-1";
  return (
    <div className="mt-5">
      <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Register as <span className="normal-case tracking-normal">— you can register as both, start with either</span></div>
      <div className="flex border border-zinc-300 mb-4">
        <button onClick={() => setRegTab("individual")} data-testid="regtab-individual"
          className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-600 ${regTab === "individual" ? "bg-primary text-white" : "bg-white hover:bg-zinc-50"}`}>
          <User className="w-4 h-4" /> Individual
        </button>
        <button onClick={() => setRegTab("business")} data-testid="regtab-business"
          className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 text-sm font-600 ${regTab === "business" ? "bg-primary text-white" : "bg-white hover:bg-zinc-50"}`}>
          <Building2 className="w-4 h-4" /> Business
        </button>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div><label className={lbl}>First name</label><input value={first} onChange={(e) => setFirst(e.target.value)} data-testid="reg-first-name" className={inp} placeholder="First name" /></div>
        <div><label className={lbl}>Last name</label><input value={last} onChange={(e) => setLast(e.target.value)} data-testid="reg-last-name" className={inp} placeholder="Last name" /></div>
        <div><label className={lbl}>Gender</label>
          <select value={gender} onChange={(e) => setGender(e.target.value)} data-testid="reg-gender" className={inp}>
            <option value="">Select…</option>{GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
          </select></div>
        <div><label className={lbl}>Year of birth</label>
          <select value={birthYear} onChange={(e) => setBirthYear(e.target.value)} data-testid="reg-birth-year" className={inp}>
            <option value="">Year</option>{YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select></div>
      </div>

      <div className="mt-4 border border-zinc-200 bg-zinc-50/60 p-3" data-testid="reg-location">
        <div className="text-xs font-600 text-zinc-600 mb-2 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-primary" /> Confirm your location</div>
        <div className="flex flex-wrap gap-2">
          <input value={loc.country} onChange={(e) => { setLoc({ ...loc, country: e.target.value }); setLocSet(false); }} data-testid="reg-loc-country" placeholder="United States" className="flex-1 min-w-[110px] border border-zinc-300 bg-white px-3 py-2 text-sm" />
          <input value={loc.region} onChange={(e) => { setLoc({ ...loc, region: e.target.value }); setLocSet(false); }} data-testid="reg-loc-region" placeholder="Oregon" className="flex-1 min-w-[90px] border border-zinc-300 bg-white px-3 py-2 text-sm" />
          <input value={loc.city} onChange={(e) => { setLoc({ ...loc, city: e.target.value }); setLocSet(false); }} data-testid="reg-loc-city" placeholder="Portland" className="flex-1 min-w-[90px] border border-zinc-300 bg-white px-3 py-2 text-sm" />
          <button onClick={() => { setLocSet(true); toast.success("Location saved to your profile"); }} data-testid="reg-loc-set"
            className={`px-4 py-2 text-xs font-900 tracking-widest ${locSet ? "bg-emerald-600 text-white" : "bg-primary text-white hover:bg-blue-700"}`}>
            {locSet ? "SET ✓" : "SET"}
          </button>
        </div>
      </div>

      {regTab === "business" && (
        <div className="mt-4 border border-primary/40 bg-blue-50/40 p-4" data-testid="reg-business-panel">
          <div className="text-[11px] font-mono uppercase tracking-widest text-primary mb-3">Business details <span className="normal-case tracking-normal text-zinc-500">— you can add more from your business profile later</span></div>
          <div className="space-y-3">
            <div><label className={lbl}>Business name <span className="text-rose-600">*</span></label>
              <input value={biz.name} onChange={(e) => setBiz({ ...biz, name: e.target.value })} data-testid="reg-biz-name" className={inp} placeholder="e.g. Portland Airport — Indigo Airlines" /></div>
            <div><label className={lbl}>Business address</label>
              <input value={biz.address} onChange={(e) => setBiz({ ...biz, address: e.target.value })} data-testid="reg-biz-address" className={inp} placeholder="Street, city — enough for customers to find you" /></div>
            <div><label className={lbl}>Contact person</label>
              <input value={biz.contact_person} onChange={(e) => setBiz({ ...biz, contact_person: e.target.value })} data-testid="reg-biz-contact" className={inp} placeholder="Auto-filled from your name — edit if different" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={lbl}>Business email</label>
                <input value={biz.business_email} onChange={(e) => setBiz({ ...biz, business_email: e.target.value })} data-testid="reg-biz-email" className={inp} placeholder="hello@portlandairport.com" /></div>
              <div><label className={lbl}>Business phone</label>
                <input value={biz.business_phone} onChange={(e) => setBiz({ ...biz, business_phone: e.target.value })} data-testid="reg-biz-phone" className={inp} placeholder="+1 555 123 4567" /></div>
            </div>
          </div>
          <p className="text-[11px] text-zinc-600 mt-3 italic">You'll sign in with your own email/phone — the business gets its own profile page where you can add team members with Manager or Editor access.</p>
        </div>
      )}
    </div>
  );
}
